/**
 * Trip labels for `/whats-on` cards — "you'll be away" vs "while you're there".
 *
 * Pure and unit-tested, same reasoning as `whatsOnBuckets.js`, `whatsOnSections.js`
 * and `tripLegs.js`: four bands render exactly what this returns, so the rules
 * should be verifiable without a database or a mounted component.
 *
 * **One mechanism, two opposite meanings.** The same trip that makes a Lisbon
 * event worth surfacing makes a home event worth discounting, so a single label
 * string cannot serve both:
 *
 *   - `away`  — the event is near home on a day the viewer is elsewhere.
 *               "You'll be in Lisbon". A warning.
 *   - `there` — the event is at the leg's place while the viewer is there.
 *               "While you're in Lisbon". An invitation.
 *
 * Get the valence wrong and a warning reads as an invitation, which is worse
 * than no label at all.
 *
 * **Never label on a guess** (brief §4). A leg with no coordinates cannot say
 * whether an event is near it, and an event with no date cannot be matched to a
 * leg at all. Both return null and the card renders exactly as it does today.
 * Silence is the default; a label has to earn itself.
 */

// How far from a leg's centre still counts as "where you are". Deliberately its
// own constant, NOT the feed's NEARBY_RADIUS_KM (161km) and not the anchor
// builder's radiusForStayLength: those answer "what should we show you", this
// answers "are you standing near this". A city and its day-trip range, no more —
// stretch it and a Lisbon leg starts claiming events in Madrid.
export const AT_LEG_RADIUS_KM = 75;

// How far from home stops being "near home". Only used to decide whether an
// event is one the viewer would plausibly have attended had they been in town,
// so it is generous: the point is to catch things they are about to miss.
export const NEAR_HOME_RADIUS_KM = 161;

const toRad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in km. Returns null if either point is incomplete. */
export function distanceKm(aLat, aLng, bLat, bLng) {
  if ([aLat, aLng, bLat, bLng].some(v => typeof v !== 'number' || !Number.isFinite(v))) {
    return null;
  }
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Does an event's run overlap a leg's stay?
 *
 * OVERLAP, not containment. A run that starts before the viewer arrives and
 * ends while they are there is on while they are there — the case that made a
 * 30-hour course spanning a month look like a bug during QA.
 */
export function overlaps(eventStart, eventEnd, legStart, legEnd) {
  if (!eventStart || !legStart || !legEnd) return false;
  const end = eventEnd || eventStart;
  return eventStart <= legEnd && end >= legStart;
}

const eventDates = (item) => {
  const start = (item?.event_date || item?.start_date || item?.date || '').slice(0, 10);
  const end = (item?.end_date || start || '').slice(0, 10);
  return { start: start || null, end: end || null };
};

const isRun = (item) => item?.content_type === 'event_group' || item?.kind === 'group';

/**
 * Is something on during this leg?
 *
 * A single event is continuous, so its span is the claim: overlap. A RUN is
 * not — its start and end are only its first and last shows, and a stay can
 * fall in the gap between them (BH-07-04: shows Nov 1 and Nov 20, a Nov 8-12
 * stay, "While you're in Paris" with nothing on). So a run is tested on SHOWS:
 * the follow bands send the ones inside the viewer's legs as `trip_show_dates`;
 * without them the only show the card vouches for is its own next one.
 */
function onDuringLeg(item, { start, end }, leg) {
  if (Array.isArray(item?.trip_show_dates)) {
    return item.trip_show_dates.some(d => overlaps(d.start, d.end, leg.startDate, leg.endDate));
  }
  if (isRun(item)) return overlaps(start, start, leg.startDate, leg.endDate);
  return overlaps(start, end, leg.startDate, leg.endDate);
}

/**
 * The label for one card, or null.
 *
 * @param {object} item      a feed-row-shaped item (needs a date and, for the
 *                           geographic tests, lat/lng)
 * @param {object} ctx
 * @param {Array}  ctx.tripLegs  from the bands payload: {place, lat, lng,
 *                               startDate, endDate}
 * @param {object} [ctx.home]    {lat, lng} — omit and `away` can never fire,
 *                               which is correct: without a home there is no
 *                               "near home" to be away from
 * @returns {{kind: 'there'|'away', place: string, startDate, endDate}|null}
 */
export function tripLabelFor(item, { tripLegs = [], home = null } = {}) {
  const dates = eventDates(item);
  if (!dates.start || tripLegs.length === 0) return null;

  const lat = typeof item?.lat === 'number' ? item.lat : item?.latitude;
  const lng = typeof item?.lng === 'number' ? item.lng : item?.longitude;

  // Legs during which something is on, soonest first (the list arrives sorted).
  const covering = tripLegs.filter(leg => onDuringLeg(item, dates, leg));
  if (covering.length === 0) return null;

  // 1. "While you're there" wins whenever it applies. An event AT the place the
  //    viewer will be is the strongest thing we can say about it, and it must
  //    never be mistaken for the warning.
  for (const leg of covering) {
    const d = distanceKm(lat, lng, leg.lat, leg.lng);
    if (d !== null && d <= AT_LEG_RADIUS_KM) {
      return { kind: 'there', place: leg.place, startDate: leg.startDate, endDate: leg.endDate };
    }
  }

  // 2. Otherwise: is this a thing near home that the viewer will miss? Requires
  //    a known home AND a known event position — "somewhere unknown while you
  //    are away" is not a claim we can make.
  const homeDist = distanceKm(lat, lng, home?.lat, home?.lng);
  if (homeDist === null || homeDist > NEAR_HOME_RADIUS_KM) return null;

  // Only if the covering leg is genuinely elsewhere. A leg without coordinates
  // cannot establish that, so it stays silent rather than guessing.
  for (const leg of covering) {
    const legFromHome = distanceKm(home?.lat, home?.lng, leg.lat, leg.lng);
    if (legFromHome !== null && legFromHome > AT_LEG_RADIUS_KM) {
      return { kind: 'away', place: leg.place, startDate: leg.startDate, endDate: leg.endDate };
    }
  }

  return null;
}

/**
 * The display form of a leg's place name.
 *
 * Legs inherit `trip_days.location`, which is typically "Paris, France" — fine
 * as data, clumsy in a sentence: "While you're in Paris, France". The leading
 * segment is the city, which is what a person would say. The same convention
 * the feed's anchor builder uses when matching day locations against
 * destination names, which disagree about the suffix.
 */
export function shortPlace(place) {
  const first = String(place || '').split(',')[0].trim();
  return first || null;
}

/**
 * Sink the events the viewer cannot attend to the bottom of their bucket.
 *
 * Only `away` items move — something near home on a day the viewer is
 * elsewhere. `there` items are left exactly where the ranker put them: this is
 * a demotion, not a promotion, and quietly boosting destination content would
 * be a ranking change nobody agreed to.
 *
 * **Demote, never suppress** (Kurtis, 2026-09-10). The item still renders, so
 * anyone who scrolls still finds it. A trip can be cancelled and a viewer may
 * want the home event regardless; demotion degrades gracefully where hiding
 * does not.
 *
 * **Per bucket, never across a band.** Callers must pass ONE bucket's items —
 * a week in band 3, one holiday in band 4. Sorting a whole band would move an
 * item between weeks, which is a lie about when it happens.
 *
 * Stable by construction: two passes preserve the ranker's relative order
 * inside each group, so undoing this restores the original list exactly.
 */
export function demoteAway(items, labelFor) {
  if (!items || items.length === 0) return items || [];
  const keep = [];
  const demoted = [];
  for (const item of items) {
    const label = labelFor ? labelFor(item) : null;
    (label && label.kind === 'away' ? demoted : keep).push(item);
  }
  return demoted.length === 0 ? items : keep.concat(demoted);
}

/**
 * What the note needs to say: how many were demoted, and WHICH places caused it.
 *
 * The places matter as much as the count. Naming `tripLegs[0]` — the soonest
 * leg — is wrong the moment a trip has two: an event demoted because the viewer
 * will be in Porto would be announced as "you'll be in Lisbon". With one place
 * the note can name it; with several it must stop claiming a destination rather
 * than pick a favourite.
 *
 * Accumulates across calls so a caller can fold several buckets into one
 * summary without flattening them first.
 */
export function awaySummary(items, labelFor, into) {
  const acc = into || { count: 0, places: [] };
  if (!items || !labelFor) return acc;
  for (const item of items) {
    const label = labelFor(item);
    if (!label || label.kind !== 'away') continue;
    acc.count += 1;
    if (label.place && !acc.places.includes(label.place)) acc.places.push(label.place);
  }
  return acc;
}

/**
 * The date to file a card under in the week buckets, or null.
 *
 * Normally the card's own start. The exception is a card the viewer can only
 * attend on a trip: labelled "while you're there", starting before that leg,
 * and far from home. A run at the destination that plays tonight and all
 * through the stay (the feed admits it for the leg) is filed from the day the
 * viewer arrives, not under "this week" beside a label about a trip weeks away.
 *
 * Near `origin`, or with no known origin, it keeps its own start: it may be
 * attendable tonight, and a bucket must not guess otherwise. Pass the point the
 * list was fetched around (the device's location before the saved home): a
 * viewer already standing in Lisbon must not have tonight's Lisbon run filed
 * under next week's Lisbon leg. Defaults to `ctx.home`. Only the bucket moves;
 * the card still shows its own dates.
 */
export function bucketDateFor(item, ctx, origin = ctx?.home) {
  const { start } = eventDates(item);
  if (!start) return null;
  const label = tripLabelFor(item, ctx);
  if (!label || label.kind !== 'there' || start >= label.startDate) return start;

  const lat = typeof item?.lat === 'number' ? item.lat : item?.latitude;
  const lng = typeof item?.lng === 'number' ? item.lng : item?.longitude;
  const originDist = distanceKm(lat, lng, origin?.lat, origin?.lng);
  if (originDist === null || originDist <= NEAR_HOME_RADIUS_KM) return start;
  return label.startDate;
}

/** Labels for a list, keyed by the same index. Convenience for a grid render. */
export function tripLabelsFor(items, ctx) {
  return (items || []).map(item => tripLabelFor(item, ctx));
}

export default tripLabelFor;
